import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { Users, Clock, MessageCircleQuestion, ClipboardList, Target, ChevronDown, ChevronRight, Bookmark, FileText, FileDown, Download, Loader2, MessageSquare, Film } from 'lucide-react';
import { getRecordingByShareToken, getTranscriptByShareToken, getNotesByShareToken, getChatByShareToken, getAssetsByShareToken, getSharedAssetDownloadUrl } from '../../services/recordingService';
import TranscriptViewer from '../user-portal/pages/recordings/TranscriptViewer';
import ChatViewer from '../user-portal/pages/recordings/ChatViewer';
import { useWhiteLabel } from '../../context/WhiteLabelContext';

const RecordingPreview = () => {
  const { fileId } = useParams();
  const playerRef = useRef(null);
  const { branding: wlBranding } = useWhiteLabel();

  const [recording, setRecording] = useState(null);
  const [allParts, setAllParts] = useState([]);
  const [activePart, setActivePart] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [rightTab, setRightTab] = useState('transcript');
  const [notes, setNotes] = useState(null);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState('summary');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showVideoDownloadMenu, setShowVideoDownloadMenu] = useState(false);
  const [downloadingVideo, setDownloadingVideo] = useState(null);
  const [videoError, setVideoError] = useState(false);

  // Multi-layout state
  const [recordingAssets, setRecordingAssets] = useState([]);
  const [activeLayoutType, setActiveLayoutType] = useState(null);

  const isMultiLayout = (recording?.rawRecordingMode === 'individual' || recording?.rawRecordingMode === 'selfhosted') && recordingAssets.length > 0;

  const activeVideoUrl = useMemo(() => {
    if (activeLayoutType && recordingAssets.length > 0) {
      const asset = recordingAssets.find(
        a => a.assetType === activeLayoutType && a.processingStatus === 'completed'
      );
      if (asset?.fileUrl) return asset.fileUrl;
    }
    return recording?.s3VideoUrl || recording?.fileUrl;
  }, [activeLayoutType, recordingAssets, recording]);

  const downloadVideo = async (part, filename) => {
    try {
      setDownloadingVideo(filename);
      // Use backend proxy to avoid S3 CORS issues
      const partId = part?._id || part?.id;
      const proxyUrl = partId && allParts.length > 1
        ? `${process.env.REACT_APP_BASE_URL}/recording/share/${fileId}/download/${partId}`
        : `${process.env.REACT_APP_BASE_URL}/recording/share/${fileId}/download`;
      const resp = await fetch(proxyUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: direct S3 URL
      const url = part?.s3VideoUrl || part?.fileUrl;
      if (url) window.open(url, '_blank');
    } finally {
      setDownloadingVideo(null);
    }
  };

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const buildNotesText = () => {
    const n = notes;
    const r = recording;
    if (!n && !r) return '';
    const lines = [`Meeting Notes: ${r?.name || 'Untitled'}`, `Date: ${r?.createdAt ? new Date(r.createdAt).toLocaleString() : 'N/A'}`, ''];
    if (n?.summary?.purpose || r?.summary) {
      lines.push('## Summary', n?.summary?.purpose || r.summary, '');
    }
    if (n?.summary?.keyTakeaways?.length || r?.keyPoints?.length) {
      lines.push('## Key Takeaways');
      (n?.summary?.keyTakeaways || r.keyPoints).forEach(p => lines.push(`  - ${p}`));
      lines.push('');
    }
    if (n?.summary?.topics?.length || r?.topics?.length) {
      lines.push('## Topics Discussed');
      (n?.summary?.topics || r.topics).forEach(t => lines.push(`  - ${t}`));
      lines.push('');
    }
    if (n?.summary?.nextSteps?.length) {
      lines.push('## Next Steps');
      n.summary.nextSteps.forEach(s => lines.push(`  - ${s}`));
      lines.push('');
    }
    if (n?.yourQuestions?.length) {
      lines.push('## Questions Asked');
      n.yourQuestions.forEach(q => {
        lines.push(`  Q: ${q.question}`);
        if (q.answer) lines.push(`  A: ${q.answer}`);
        lines.push('');
      });
    }
    if (n?.theirQuestions?.length) {
      lines.push('## Their Questions');
      n.theirQuestions.forEach(q => {
        lines.push(`  Q: ${q.question} (asked by ${q.askedBy})`);
        if (q.answer) lines.push(`  A: ${q.answer}`);
        lines.push('');
      });
    }
    if (n?.assignments?.length || r?.actionItems?.length) {
      lines.push('## Action Items');
      if (n?.assignments?.length) {
        n.assignments.forEach(a => lines.push(`  [ ] ${a.task} (assigned to: ${a.assignee})`));
      } else {
        r.actionItems?.forEach(a => lines.push(`  [ ] ${a}`));
      }
      lines.push('');
    }
    return lines.join('\n');
  };

  const buildTranscriptText = () => {
    if (!transcript.length) return '';
    const formatTs = (ms) => {
      const totalSec = Math.floor(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };
    return transcript.map(seg => {
      const speaker = seg.speakerName || seg.speaker || 'Unknown';
      const ts = seg.startMs ?? seg.timestamp ?? 0;
      return `[${formatTs(ts)}] ${speaker}: ${seg.text}`;
    }).join('\n');
  };

  const handleDownloadNotes = () => {
    const text = buildNotesText();
    const safeName = (recording?.name || 'meeting').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadFile(text, `${safeName}_notes.txt`);
    setShowDownloadMenu(false);
  };

  const handleDownloadTranscript = () => {
    const text = buildTranscriptText();
    const safeName = (recording?.name || 'meeting').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadFile(text, `${safeName}_transcript.txt`);
    setShowDownloadMenu(false);
  };

  useEffect(() => {
    if (fileId) {
      loadRecordingData();
    }
  }, [fileId]);

  const loadRecordingData = async () => {
    try {
      setLoading(true);
      const [recData, transData, notesData, chatData, assetsData] = await Promise.all([
        getRecordingByShareToken(fileId),
        getTranscriptByShareToken(fileId).catch(() => ({ segments: [] })),
        getNotesByShareToken(fileId).catch(() => ({ notes: null })),
        getChatByShareToken(fileId).catch(() => ({ messages: [] })),
        getAssetsByShareToken(fileId).catch(() => ({ assets: [] })),
      ]);

      if (recData?.recording) {
        // The backend overrides recData.recording.shareSettings with the per-share
        // settings (effectiveSettings). Items in allParts are raw recording docs
        // and still carry the *recording-level* shareSettings — propagate the
        // per-share settings onto each part so all UI gating respects this share's permissions.
        const effectiveSettings =
          recData.effectiveSettings || recData.recording?.shareSettings || {};
        let parts = (recData.allParts || [recData.recording]).map((p) => ({
          ...p,
          shareSettings: effectiveSettings,
        }));
        // Filter parts based on share settings (includedParts)
        const included = effectiveSettings?.includedParts;
        if (included && included.length > 0 && parts.length > 1) {
          parts = parts.filter((_, idx) => included.includes(idx));
        }
        setAllParts(parts);
        setRecording(parts[0]);
        setActivePart(0);

        // Multi-layout assets: pick the best default layout to display
        const assets = assetsData?.assets || [];
        setRecordingAssets(assets);
        const primary = parts[0];
        if (primary.rawRecordingMode === 'individual' || primary.rawRecordingMode === 'selfhosted') {
          const preferred = ['gallery_view', 'focus', 'speaker_top', 'speaker_side'];
          let pick = null;
          if (primary.primaryAssetType) {
            const a = assets.find(x => x.assetType === primary.primaryAssetType && x.processingStatus === 'completed');
            if (a) pick = primary.primaryAssetType;
          }
          if (!pick) {
            for (const t of preferred) {
              if (assets.find(x => x.assetType === t && x.processingStatus === 'completed')) { pick = t; break; }
            }
          }
          if (pick) setActiveLayoutType(pick);
        }
      } else {
        setError('Recording not found or not publicly shared');
      }
      setTranscript(transData?.segments || []);
      setChatMessages(chatData?.messages || []);
      setNotes(notesData?.notes || null);
    } catch (err) {
      console.error('Error fetching recording:', err);
      const apiMessage = err?.response?.data?.message;
      setError(apiMessage || 'Failed to load recording');
    } finally {
      setLoading(false);
    }
  };

  const handleProgress = (state) => {
    setCurrentTimeMs(state.playedSeconds * 1000);
  };

  const handleSeek = (timeMs) => {
    if (playerRef.current) {
      playerRef.current.seekTo(timeMs / 1000, 'seconds');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#001233]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00c853]"></div>
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#001233] text-white">
        <p className="text-xl mb-4">{error || 'Recording not found'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#001233] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#001233]/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-semibold break-words">{recording.name}</h1>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs sm:text-sm text-white/60">
                {recording.duration > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {formatDuration(recording.duration)}
                  </span>
                )}
                {recording.participants?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {recording.participants.length} participants
                  </span>
                )}
                <span>{new Date(recording.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' })}</span>
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-2 sm:gap-3">
              {/* Download video button — only if allowed */}
              {recording.shareSettings?.allowDownload !== false && allParts.some(p => p.s3VideoUrl || p.fileUrl) && (
                <div className="relative">
                  {allParts.length === 1 ? (
                    <button
                      onClick={() => downloadVideo(
                        recording,
                        `${recording.name || 'recording'}.mp4`
                      )}
                      disabled={!!downloadingVideo}
                      className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-2 text-sm text-white disabled:opacity-50"
                    >
                      {downloadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {downloadingVideo ? 'Downloading...' : 'Video'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => !downloadingVideo && setShowVideoDownloadMenu(!showVideoDownloadMenu)}
                        disabled={!!downloadingVideo}
                        className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-2 text-sm text-white disabled:opacity-50"
                      >
                        {downloadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {downloadingVideo ? 'Downloading...' : 'Video'}
                        {!downloadingVideo && <ChevronDown className="w-3 h-3" />}
                      </button>
                      {showVideoDownloadMenu && (
                        <>
                          <div className="fixed inset-0 z-20" onClick={() => setShowVideoDownloadMenu(false)} />
                          <div className="absolute right-0 mt-2 w-52 bg-[#001233] border border-white/20 rounded-lg shadow-xl z-30 overflow-hidden">
                            {allParts.map((part, idx) => {
                              if (!part.s3VideoUrl && !part.fileUrl) return null;
                              const name = `${recording.name || 'recording'} - Part ${idx + 1}.mp4`;
                              return (
                                <button
                                  key={part._id}
                                  onClick={() => { setShowVideoDownloadMenu(false); downloadVideo(part, name); }}
                                  disabled={downloadingVideo === name}
                                  className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors disabled:opacity-50"
                                >
                                  <Download className="w-4 h-4 text-purple-400" />
                                  Part {idx + 1}
                                  {part.duration > 0 && (
                                    <span className="text-xs text-white/40 ml-auto">{formatDuration(part.duration)}</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
              {/* Export dropdown — only if allowed */}
              {recording.shareSettings?.allowExport !== false && (
                <div className="relative">
                  <button
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-2 text-sm"
                  >
                    <FileDown className="w-4 h-4" />
                    Export
                  </button>
                  {showDownloadMenu && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setShowDownloadMenu(false)} />
                      <div className="absolute right-0 mt-2 w-48 bg-[#001233] border border-white/20 rounded-lg shadow-xl z-30 overflow-hidden">
                        {recording.shareSettings?.showAINotes === true && (notes || recording.summary) && (
                          <button
                            onClick={handleDownloadNotes}
                            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                          >
                            <FileText className="w-4 h-4 text-[#00c853]" />
                            Meeting Notes
                          </button>
                        )}
                        {transcript.length > 0 && (
                          <button
                            onClick={handleDownloadTranscript}
                            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                          >
                            <ClipboardList className="w-4 h-4 text-blue-400" />
                            Transcript
                          </button>
                        )}
                        {(recording.shareSettings?.showAINotes !== true || (!notes && !recording.summary)) && transcript.length === 0 && (
                          <div className="px-4 py-2.5 text-sm text-white/40">No data to export</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* R-Link branding */}
              <div className="text-sm text-white/40 hidden md:block">
                Shared via <span className="bg-gradient-to-r from-[#6a1fbf] to-[#00c853] bg-clip-text text-transparent font-semibold">{wlBranding.companyName || 'R-Link Studio'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column: Video + Notes */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Part selector — show when multiple parts */}
            {allParts.length > 1 && (
              <div className="flex items-center gap-2 overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 pb-1">
                {allParts.map((part, idx) => (
                  <button
                    key={part._id}
                    onClick={() => { setActivePart(idx); setRecording(part); }}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                      activePart === idx
                        ? 'bg-purple-600 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
                    }`}
                  >
                    Part {idx + 1}
                    {part.duration > 0 && (
                      <span className="ml-2 text-xs opacity-70">
                        ({formatDuration(part.duration)})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Video Player — only show if there's a video URL and recording didn't fail */}
            {recording.status === 'failed' || videoError ? (
              <div className="bg-white/5 backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 text-center">
                <Film className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-1">Video Unavailable</h3>
                <p className="text-sm text-white/50">
                  {recording.status === 'failed'
                    ? 'This recording failed to process. The video file was not saved successfully.'
                    : 'The video file could not be loaded. It may have been removed or failed to upload.'}
                </p>
              </div>
            ) : activeVideoUrl ? (
              <>
                {/* Layout selector — only shown for multi-layout recordings */}
                {isMultiLayout && (
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    {[
                      { type: 'gallery_view', label: 'Gallery', icon: '◫' },
                      { type: 'focus', label: 'Focus', icon: '▢' },
                      { type: 'speaker_top', label: 'Speaker Top', icon: '▬' },
                      { type: 'speaker_side', label: 'Speaker Side', icon: '◧' },
                      { type: 'screen_speaker', label: 'Screen + Speaker', icon: '◨', needsScreenShare: true },
                      { type: 'screen_gallery', label: 'Screen + Gallery', icon: '⊞', needsScreenShare: true },
                      { type: 'screen_only', label: 'Screen Only', icon: '▣', needsScreenShare: true },
                    ].filter(layout => {
                      if (layout.needsScreenShare && !recording?.hasScreenShare) return false;
                      return recordingAssets.some(a => a.assetType === layout.type && a.processingStatus === 'completed');
                    }).map(layout => {
                      const isActive = activeLayoutType === layout.type;
                      return (
                        <button
                          key={layout.type}
                          onClick={() => setActiveLayoutType(layout.type)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                            isActive
                              ? 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white shadow-lg'
                              : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
                          }`}
                        >
                          <span className="text-xs">{layout.icon}</span>
                          {layout.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="bg-black rounded-2xl overflow-hidden">
                  <ReactPlayer
                    key={activeLayoutType || 'default'}
                    ref={playerRef}
                    url={activeVideoUrl}
                    width="100%"
                    height="auto"
                    controls
                    onProgress={handleProgress}
                    onError={() => setVideoError(true)}
                    config={{
                      file: {
                        attributes: {
                          controlsList: 'nodownload',
                        },
                      },
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
                <FileText className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-1">Meeting Notes</h3>
                <p className="text-sm text-white/50">This meeting has transcript and AI notes but no video recording</p>
              </div>
            )}

            {/* Recording Files list (Zoom-style) — multi-layout only */}
            {isMultiLayout && recordingAssets.length > 0 && recording.shareSettings?.allowDownload !== false && (() => {
              const formatBytes = (bytes) => {
                if (!bytes) return '—';
                if (bytes < 1024) return `${bytes} B`;
                if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
                if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
                return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
              };
              const videoLayouts = [
                { type: 'screen_speaker', label: 'Shared screen with speaker view', needsScreenShare: true },
                { type: 'screen_gallery', label: 'Shared screen with gallery view', needsScreenShare: true },
                { type: 'speaker_top', label: 'Speaker top' },
                { type: 'speaker_side', label: 'Speaker side' },
                { type: 'focus', label: 'Focus view' },
                { type: 'gallery_view', label: 'Gallery view' },
                { type: 'screen_only', label: 'Shared screen', needsScreenShare: true },
              ];
              const audioOnlyAsset = recordingAssets.find(a => a.assetType === 'audio_only' && a.processingStatus === 'completed');
              const participantAudios = recordingAssets.filter(a => a.assetType === 'participant_audio' && a.processingStatus === 'completed');

              return (
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10">
                    <h3 className="text-sm font-semibold text-white/90">The recording includes the files listed below</h3>
                  </div>
                  <div className="divide-y divide-white/10">
                    {videoLayouts.filter(l => {
                      if (l.needsScreenShare && !recording?.hasScreenShare) return false;
                      return recordingAssets.some(a => a.assetType === l.type && a.processingStatus === 'completed');
                    }).map(layout => {
                      const asset = recordingAssets.find(a => a.assetType === layout.type);
                      return (
                        <div key={layout.type} className="px-6 py-3 flex items-center gap-3 hover:bg-white/5">
                          <Film className="w-4 h-4 text-white/40 flex-shrink-0" />
                          <button
                            onClick={() => setActiveLayoutType(layout.type)}
                            className={`text-sm flex-1 text-left ${activeLayoutType === layout.type ? 'text-[#00c853] font-semibold' : 'text-white hover:text-[#00c853]'}`}
                          >
                            {layout.label}
                            {activeLayoutType === layout.type && <span className="ml-2 text-xs">(Playing)</span>}
                          </button>
                          <span className="text-xs text-white/40 tabular-nums">{formatBytes(asset?.fileSize)}</span>
                          <a href={getSharedAssetDownloadUrl(fileId, asset.id)} className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white" title="Download">
                            <Download className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      );
                    })}

                    {audioOnlyAsset && (
                      <div className="px-6 py-3 flex items-center gap-3 hover:bg-white/5">
                        <span className="text-white/40">🎵</span>
                        <span className="text-sm text-white flex-1">Audio only</span>
                        <span className="text-xs text-white/40 tabular-nums">{formatBytes(audioOnlyAsset.fileSize)}</span>
                        <a href={getSharedAssetDownloadUrl(fileId, audioOnlyAsset.id)} className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white" title="Download">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    )}

                    {participantAudios.length > 0 && (
                      <div className="px-6 py-3">
                        <div className="text-sm text-white/90 font-medium mb-2 flex items-center gap-2">
                          <ChevronDown className="w-4 h-4" />
                          Audio file of each participant
                        </div>
                        <div className="pl-6 space-y-2">
                          {participantAudios.map(asset => (
                            <div key={asset.id} className="flex items-center gap-3">
                              <span className="text-white/40">🎵</span>
                              <span className="text-sm text-white/80 flex-1">
                                Audio only - {asset.participantName || `Participant ${asset.participantUid}`}
                              </span>
                              <span className="text-xs text-white/40 tabular-nums">{formatBytes(asset.fileSize)}</span>
                              <a href={getSharedAssetDownloadUrl(fileId, asset.id)} className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white" title="Download">
                                <Download className="w-3.5 h-3.5" />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* AI Meeting Notes — hidden by default for public links, shown when showAINotes is enabled */}
            {recording.shareSettings?.showAINotes === true && (recording.summary || notes) && (
              <div className="space-y-4">
                {/* Summary Section */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'summary' ? null : 'summary')}
                    className="w-full flex items-center justify-between"
                  >
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#00c853]" />
                      Summary
                    </h2>
                    {expandedSection === 'summary' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                  </button>
                  {expandedSection === 'summary' && (
                    <div className="mt-4 space-y-4">
                      {/* Meeting Purpose */}
                      {(notes?.summary?.purpose || recording.summary) && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Meeting Purpose</h4>
                          <p className="text-sm text-white/90">{notes?.summary?.purpose || recording.summary}</p>
                        </div>
                      )}

                      {/* Key Takeaways */}
                      {(notes?.summary?.keyTakeaways?.length > 0 || recording.keyPoints?.length > 0) && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Key Takeaways</h4>
                          <ul className="space-y-1.5">
                            {(notes?.summary?.keyTakeaways || recording.keyPoints).map((point, idx) => (
                              <li key={idx} className="text-sm text-white/80 flex items-start gap-2">
                                <span className="text-[#00c853] mt-0.5 flex-shrink-0">•</span>
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Topics */}
                      {(notes?.summary?.topics?.length > 0 || recording.topics?.length > 0) && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Topics Discussed</h4>
                          <div className="flex flex-wrap gap-2">
                            {(notes?.summary?.topics || recording.topics).map((topic, idx) => (
                              <span key={idx} className="px-3 py-1 bg-[#6a1fbf]/20 border border-[#6a1fbf]/30 rounded-full text-xs text-white/80">
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Next Steps */}
                      {notes?.summary?.nextSteps?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Next Steps</h4>
                          <ul className="space-y-1.5">
                            {notes.summary.nextSteps.map((step, idx) => (
                              <li key={idx} className="text-sm text-white/80 flex items-start gap-2">
                                <span className="text-[#6a1fbf] mt-0.5 flex-shrink-0">→</span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Your Questions */}
                {notes?.yourQuestions?.length > 0 && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'yourQuestions' ? null : 'yourQuestions')}
                      className="w-full flex items-center justify-between"
                    >
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <MessageCircleQuestion className="w-5 h-5 text-blue-400" />
                        Questions Asked ({notes.yourQuestions.length})
                      </h2>
                      {expandedSection === 'yourQuestions' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                    </button>
                    {expandedSection === 'yourQuestions' && (
                      <div className="mt-4 space-y-3">
                        {notes.yourQuestions.map((q, idx) => (
                          <div key={idx} className="p-3 bg-blue-400/5 border border-blue-400/10 rounded-xl">
                            <p className="text-sm font-medium text-blue-300 mb-1">Q: {q.question}</p>
                            {q.answer && (
                              <p className="text-sm text-white/70 pl-4 border-l-2 border-blue-400/30">A: {q.answer}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Their Questions */}
                {notes?.theirQuestions?.length > 0 && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'theirQuestions' ? null : 'theirQuestions')}
                      className="w-full flex items-center justify-between"
                    >
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <MessageCircleQuestion className="w-5 h-5 text-purple-400" />
                        Their Questions ({notes.theirQuestions.length})
                      </h2>
                      {expandedSection === 'theirQuestions' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                    </button>
                    {expandedSection === 'theirQuestions' && (
                      <div className="mt-4 space-y-3">
                        {notes.theirQuestions.map((q, idx) => (
                          <div key={idx} className="p-3 bg-purple-400/5 border border-purple-400/10 rounded-xl">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-purple-300">Q: {q.question}</p>
                            </div>
                            <p className="text-[10px] text-white/40 mb-1">Asked by {q.askedBy}</p>
                            {q.answer && (
                              <p className="text-sm text-white/70 pl-4 border-l-2 border-purple-400/30">A: {q.answer}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Assignments / Action Items */}
                {(notes?.assignments?.length > 0 || recording.actionItems?.length > 0) && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'actions' ? null : 'actions')}
                      className="w-full flex items-center justify-between"
                    >
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-yellow-400" />
                        Action Items ({notes?.assignments?.length || recording.actionItems?.length || 0})
                      </h2>
                      {expandedSection === 'actions' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                    </button>
                    {expandedSection === 'actions' && (
                      <div className="mt-4 space-y-2">
                        {notes?.assignments?.length > 0 ? (
                          notes.assignments.map((item, idx) => (
                            <div key={idx} className="p-3 bg-yellow-400/5 border border-yellow-400/10 rounded-xl flex items-start gap-3">
                              <input type="checkbox" className="mt-1 accent-yellow-400" readOnly />
                              <div>
                                <p className="text-sm text-white/90">{item.task}</p>
                                <p className="text-[10px] text-white/40 mt-0.5">Assigned to: {item.assignee}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          recording.actionItems?.map((item, idx) => (
                            <div key={idx} className="p-3 bg-yellow-400/5 border border-yellow-400/10 rounded-xl flex items-start gap-3">
                              <input type="checkbox" className="mt-1 accent-yellow-400" readOnly />
                              <p className="text-sm text-white/90">{item}</p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Transcript / Chat */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)] max-h-[70vh] flex flex-col overflow-hidden">
              {/* Tab switcher */}
              <div className="flex items-center gap-1 mb-4 flex-shrink-0 bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setRightTab('transcript')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                    rightTab === 'transcript' ? 'bg-purple-600 text-white' : 'text-white/50 hover:text-white/80'
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  Transcript
                </button>
                {recording.shareSettings?.showChat === true && (
                  <button
                    onClick={() => setRightTab('chat')}
                    className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                      rightTab === 'chat' ? 'bg-purple-600 text-white' : 'text-white/50 hover:text-white/80'
                    }`}
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    Chat
                    {chatMessages.length > 0 && (
                      <span className="text-[10px] bg-white/20 rounded-full px-1.5">{chatMessages.length}</span>
                    )}
                  </button>
                )}
              </div>

              {rightTab === 'transcript' ? (
                <TranscriptViewer
                  segments={transcript}
                  currentTimeMs={currentTimeMs}
                  bookmarks={[]}
                  recordingCreatedAt={recording?.createdAt}
                  recordingDurationSec={recording?.duration}
                  recordingStartedAt={recording?.recordingStartedAt}
                  onSeek={handleSeek}
                />
              ) : (
                chatMessages.length > 0 ? (
                  <ChatViewer
                    messages={chatMessages}
                    currentTimeMs={currentTimeMs}
                    recordingCreatedAt={recording?.createdAt}
                    onSeek={handleSeek}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-white/40">
                    <MessageSquare className="w-10 h-10 mb-3 opacity-40" />
                    <p className="text-sm">No chat messages</p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordingPreview;
